const API_BASE = import.meta.env.VITE_API_URL || ''

class ApiClient {
  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.detail || data.error || `HTTP error ${response.status}`)
    }

    return data
  }

  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' })
  }

  post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  put(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body)
    })
  }

  patch(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body)
    })
  }

  delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' })
  }
}

export const api = new ApiClient()

// Utility for formatting currency
export function formatCurrency(amount) {
  if (amount == null) return '-'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount)
}

// Utility for formatting dates
export function formatDate(dateString) {
  if (!dateString) return '-'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(new Date(dateString))
}

export function formatDateTime(dateString) {
  if (!dateString) return '-'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(dateString))
}

// Status badge class helper
export function getStatusBadgeClass(status) {
  const statusMap = {
    // Deals
    quote_requested: 'badge-info',
    quote_ready: 'badge-info',
    quote_sent: 'badge-success',
    quote_expiring: 'badge-warning',
    awaiting_shipment: 'badge-info',
    shipping_arranged: 'badge-info',
    arrived_to_test: 'badge-info',
    tested: 'badge-success',
    issue_with_item: 'badge-error',
    awaiting_payment: 'badge-warning',
    closed_traded: 'badge-success',
    closed_lost: 'badge-neutral',

    // Inventory
    draft: 'badge-neutral',
    active: 'badge-success',
    sold: 'badge-info',
    archived: 'badge-neutral',

    // Orders
    pending: 'badge-warning',
    confirmed: 'badge-info',
    processing: 'badge-info',
    shipped: 'badge-success',
    delivered: 'badge-success',
    cancelled: 'badge-error',
    refunded: 'badge-neutral',

    // Warehouse
    receiving: 'badge-info',
    testing: 'badge-info',
    repairs: 'badge-warning',
    issue_trades: 'badge-error',
    putaway: 'badge-info',
    photography: 'badge-info',
    ready_to_ship: 'badge-success',

    // Repairs
    not_started: 'badge-neutral',
    in_progress: 'badge-info',
    complete: 'badge-success',
    not_repaired: 'badge-error',
    listed: 'badge-success'
  }

  return statusMap[status] || 'badge-neutral'
}

export function formatStatus(status) {
  if (!status) return '-'
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
}
